import moment from 'moment';
import OperatingHoursMapper from '@/classes/OperatingHoursMapper';
import { InstructorTableRowType } from '@/enums/InstructorTableRowType';
import { InstructorTableCellType } from '@/enums/InstructorTableCellType';
import InstructorTableInstructorRow from '@/classes/InstructorTableInstructorRow';
import InstructorTableBlockRow from '@/classes/InstructorTableBlockRow';
import InstructorTableAvailableCell from '@/classes/InstructorTableAvailableCell';
import InstructorTableReservedCell from '@/classes/InstructorTableReservedCell';
import InstructorTablePauseCell from '@/classes/InstructorTablePauseCell';
import store from '@/store/index';
import InstructorTableKidSchool from '@/classes/InstructorTableKidSchool';
import InstructorTableKidSchoolCell from './InstructorTableKidSchoolCell';
import { callFn } from '@/utils/functionsClient';
export default class InstructorTable {
    id;
    date;
    changedAt;
    fromHour;
    toHour;
    concluded;
    pauseHours = [];
    rows = [];
    kidSchools = [];
    toSaveObject() {
        return {
            date: this.date.format('YYYY-MM-DD'),
            concluded: this.concluded,
            changedAt: moment().toISOString(),
            rows: this.rows.map(x => x.toSaveObject())
        };
    }
    getOrderedRows(showOtherInstructors) {
        let result = this.rows;
        result = result.filter(x => x.rowType === InstructorTableRowType.BLOCK || x.cells.some(y => y.cellType !== InstructorTableCellType.PAUSE)
            || (x.rowType === InstructorTableRowType.INSTRUCTOR && x.isCurrentUserRow()));
        if (!showOtherInstructors) {
            result = result.filter((x) => x.rowType !== InstructorTableRowType.INSTRUCTOR ||
                x.isCurrentUserRow());
        }
        return result.sort((a, b) => {
            const showCurrentUserOnFirstPlace = false;
            let aType;
            let bType;
            aType = a.rowType;
            bType = b.rowType;
            const currentUser = 'currentUser';
            if (showCurrentUserOnFirstPlace && a.rowType === InstructorTableRowType.INSTRUCTOR &&
                a.isCurrentUserRow()) {
                aType = currentUser;
            }
            if (showCurrentUserOnFirstPlace && b.rowType === InstructorTableRowType.INSTRUCTOR &&
                b.isCurrentUserRow()) {
                bType = currentUser;
            }
            switch (aType + bType) {
                case `${InstructorTableRowType.BLOCK}${InstructorTableRowType.BLOCK}`:
                    return (a.blockId < b.blockId) ? -1 : 1;
                case `${InstructorTableRowType.INSTRUCTOR}${InstructorTableRowType.INSTRUCTOR}`:
                    return (a.addedDate < b.addedDate) ? -1 : 1;
                case `${currentUser}${InstructorTableRowType.BLOCK}`:
                case `${currentUser}${InstructorTableRowType.INSTRUCTOR}`:
                case `${InstructorTableRowType.INSTRUCTOR}${InstructorTableRowType.BLOCK}`: {
                    return -1;
                }
                case `${InstructorTableRowType.BLOCK}${InstructorTableRowType.INSTRUCTOR}`:
                case `${InstructorTableRowType.BLOCK}${currentUser}`:
                case `${InstructorTableRowType.INSTRUCTOR}${currentUser}`:
                    return 1;
            }
            return 0;
        });
    }
    async conclude() {
        const dateKey = this.date.format('YYYY-MM-DD');
        const kidSchools = this.kidSchools.map(ks => ({
            id: ks.id,
            durationMinutes: ks.duration,
            isPremium: ks.isPremium || false,
            leaderId: ks.leaderId,
            hourFrom: ks.hourFrom,
        }));
        await callFn('concludeDay', {
            date: dateKey,
            expectedVersion: store.getters['instructorTable/getVersion'](dateKey),
            kidSchools,
        });
    }
    static loadFromObject(object, id) {
        const result = new InstructorTable();
        result.id = id;
        result.date = moment(object.date, 'YYYY-MM-DD');
        result.changedAt = object.changedAt ?? '';
        result.concluded = object.concluded;
        const hours = OperatingHoursMapper.getByDay(result.date);
        result.fromHour = hours.openFrom;
        result.toHour = hours.openTo;
        result.pauseHours = hours.pauses;
        result.rows = object.rows?.map((x) => this.loadRowFromObject(x, result, result.pauseHours ?? [])) ?? [];
        result.kidSchools = object.kidSchools?.map((x) => this.loadKidSchoolFromObject(x, result.rows.filter(z => z.rowType === InstructorTableRowType.INSTRUCTOR
            && this.findKidSchoolCell(z, x.id)))) ?? [];
        if (!result.rows.filter(x => x.rowType ===
            InstructorTableRowType.INSTRUCTOR).some(x => x.instructorId === store.getters['user/userId'])) {
            const currentUserRow = new InstructorTableInstructorRow(result);
            currentUserRow.instructorId = store.getters['user/userId'];
            currentUserRow.addedDate = moment();
            currentUserRow.cells = [];
            result.rows.push(this.loadRowFromObject(currentUserRow, result, hours.pauses));
        }
        return result;
    }
    static loadRowFromObject(object, table, pauses) {
        switch (object.rowType) {
            case InstructorTableRowType.INSTRUCTOR:
                {
                    let row = new InstructorTableInstructorRow(table);
                    row.addedDate = moment(object.addedDate, 'YYYY-MM-DD HH:mm:ss');
                    row.instructorId = object.instructorId;
                    row.cells = [
                        ...object.cells.map((x) => this.loadCellFromObject(x, row)),
                        ...pauses.map(x => {
                            const result = new InstructorTablePauseCell(row);
                            result.hour = x;
                            return result;
                        }),
                    ];
                    return row;
                }
            case InstructorTableRowType.BLOCK:
                {
                    let row = new InstructorTableBlockRow(table);
                    row.blockId = object.blockId;
                    row.cells = [
                        ...object.cells.map((x) => this.loadCellFromObject(x, row)),
                        ...pauses.map(x => { const result = new InstructorTablePauseCell(row); result.hour = x; return result; })
                    ];
                    return row;
                }
            default:
                throw new Error('Undefined type');
        }
    }
    static loadCellFromObject(object, row) {
        switch (object.cellType) {
            case InstructorTableCellType.AVAILABLE:
                {
                    let result = new InstructorTableAvailableCell(row);
                    result.hour = object.hour;
                    return result;
                }
            case InstructorTableCellType.RESERVED:
                {
                    let result = new InstructorTableReservedCell(row);
                    result.hour = object.hour;
                    result.clients = object.clients;
                    result.length = object.length;
                    result.service = object.service;
                    result.note = object.note;
                    result.phone = object.phone;
                    result.isPrepaid = object.isPrepaid ?? false;
                    result.payments = object.payments ?? [];
                    result.payment = object.payment ?? null;
                    result.voucher = object.voucher
                        ? store.getters['voucher/get'](object.voucher)
                        : null;
                    result.voucherBlockedValue = object.voucherBlockedValue ?? 0;
                    return result;
                }
            case InstructorTableCellType.PAUSE:
                {
                    let result = new InstructorTablePauseCell(row);
                    result.hour = object.hour;
                    return result;
                }
            case InstructorTableCellType.KID_SCHOOL:
                {
                    let result = new InstructorTableKidSchoolCell();
                    result.hour = object.hour;
                    result.kidSchoolId = object.kidSchoolId;
                    result.length = object.length;
                    result.isLeader = object.isLeader ?? false;
                    return result;
                }
            default:
                throw new Error('Undefined type');
        }
    }
    static findKidSchoolCell(row, kidSchoolId) {
        return row.cells.find(x => x.cellType === InstructorTableCellType.KID_SCHOOL
            && x.kidSchoolId === kidSchoolId);
    }
    static loadKidSchoolFromObject(object, rows) {
        let result = new InstructorTableKidSchool();
        result.id = object.id;
        result.duration = object.length;
        result.hourFrom = moment(object.dateTime).hour();
        result.minuteFrom = moment(object.dateTime).minute();
        result.capacity = object.capacity;
        result.instructorMaxCount = object.instructorsCount;
        result.instructorIds = rows.map(x => x.instructorId);
        result.leaderId = rows.find(x => this.findKidSchoolCell(x, object.id)?.isLeader)?.instructorId;
        result.kids = object.kids;
        result.finalKidCount = object.finalKidCount;
        result.pricePerKid = object.price;
        result.isPremium = object.isPremium;
        result.isPrepaidOnly = object.isPrepaidOnly;
        return result;
    }
}
